import { DialogResults, ModalRef, useTranslation } from '@msdk/common'
import {
  Box,
  ButtonIcon,
  ButtonPrimary,
  CloseIcon,
  COLORS,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  IconSelector,
  Markdown,
  styled,
  THEME_CONST,
  Typography,
  uiTokens,
  useComponentLibrary,
} from '@msdk/ui'

const MuiStyledDialogTitle = styled(DialogTitle)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${COLORS.grey50};
  max-height: 56px;
  padding: 16px 24px;
`
const NovaStyledDialogTitle = styled(DialogTitle)`
  justify-content: space-between;
  height: ${uiTokens.sizing.uiControl.height.large}px;
  padding: ${uiTokens.spacing.padding.semiMedium}px ${uiTokens.spacing.padding.semiMedium}px
    ${uiTokens.spacing.padding.small}px ${uiTokens.spacing.padding.medium}px;
  gap: ${uiTokens.spacing.gap.small};
`

const NovaStyledTitleTypography = styled(Typography)`
  font-weight: ${uiTokens.font.fontWeight.semiBold};
  font-size: ${uiTokens.font.fontSize.title.large}px;
  line-height: ${uiTokens.font.lineHeight.title.large}px;
  letter-spacing: ${uiTokens.font.letterSpacing.normal}px;
  color: ${uiTokens.uiContent.emphasized};
`

const MuiStyledMessageTypography = styled(Typography)`
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: pre-line;
`

const NovaStyledMessageTypography = styled(Typography)`
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: pre-line;
  font-weight: ${uiTokens.font.fontWeight.regular};
  font-size: ${uiTokens.font.fontSize.body.large}px;
  line-height: ${uiTokens.font.lineHeight.body.large}px;
  letter-spacing: ${uiTokens.font.letterSpacing.normal}px;
  color: ${uiTokens.uiContent.emphasized};
`

const MuiContent = styled(Box)`
  padding: ${THEME_CONST.defaultPadding}px;
  overflow-y: auto;
`
const NovaContent = styled(Box)`
  padding-right: ${uiTokens.spacing.padding.medium}px;
  padding-bottom: ${uiTokens.spacing.padding.xSmall}px;
  padding-left: ${uiTokens.spacing.padding.medium}px;
  overflow-y: auto;
`

interface ModalProps {
  modalRef?: ModalRef
}

export function DataRetentionDialog(props: ModalProps) {
  const library = useComponentLibrary()
  const t = useTranslation()
  const dialogTitle = t('LBL_CONFIGURATION_REQUIRED_TITLE', 'Configuration required')
  const dialogMessage = t(
    'LBL_CONFIGURATION_REQUIRED_MSG',
    'The **Retention and Reinstatement settings** must be confirmed or edited.  \n  Without this configuration, users will not be able to work with the product.',
  )
  const dialogButton = t('LBL_CONFIGURATION_REQUIRED_BTN', 'Go to settings')

  const MuiDataRetentionDialog = (
    <Dialog open={props.modalRef?.status === 'Open'}>
      <MuiStyledDialogTitle>
        <Typography variant="h6">{dialogTitle}</Typography>
        <ButtonIcon onClick={() => props.modalRef?.close(DialogResults.Ok)} aria-label="close">
          <CloseIcon />
        </ButtonIcon>
      </MuiStyledDialogTitle>
      <Divider />
      <MuiContent>
        <MuiStyledMessageTypography variant="body1" align="left" component="div">
          <Markdown>{dialogMessage}</Markdown>
        </MuiStyledMessageTypography>
      </MuiContent>
      <Divider />
      <DialogActions>
        <ButtonPrimary key={DialogResults.Ok} onClick={() => props.modalRef?.close(DialogResults.Ok)}>
          <Typography variant="button">{dialogButton}</Typography>
          <IconSelector iconName="KeyboardArrowRight" />
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  )

  const NovaDataRetentionDialog = (
    <Dialog open={props.modalRef?.status === 'Open'}>
      <NovaStyledDialogTitle>
        <NovaStyledTitleTypography variant="h6">{dialogTitle}</NovaStyledTitleTypography>
        <ButtonIcon onClick={() => props.modalRef?.close(DialogResults.Ok)} aria-label="close">
          <CloseIcon />
        </ButtonIcon>
      </NovaStyledDialogTitle>
      <NovaContent>
        <NovaStyledMessageTypography variant="body1" align="left" component="div">
          <Markdown>{dialogMessage}</Markdown>
        </NovaStyledMessageTypography>
      </NovaContent>
      <DialogActions>
        <ButtonPrimary key={DialogResults.Ok} onClick={() => props.modalRef?.close(DialogResults.Ok)}>
          <Typography variant="button">{dialogButton}</Typography>
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  )
  return library === 'mui' ? MuiDataRetentionDialog : NovaDataRetentionDialog
}
